/**
 * Cashfree Payment Gateway Utility
 * Used by both SubscriptionPage (B2B plan purchase) and CheckoutPage (B2C food orders)
 *
 * Cashfree Docs: https://docs.cashfree.com/docs/javascript-integration
 * 
 * NOTE: We use redirectTarget: '_self' (full page redirect) instead of '_modal' (iframe)
 * because _modal gets blocked by ad blockers in production environments.
 */

const CASHFREE_ENV = 'production'; // Forced to production to prevent stale env bugs

// ─── Load Cashfree JS SDK ──────────────────────────────────────────────────

let cashfreeSDKPromise = null;

export function loadCashfreeSDK() {
    if (cashfreeSDKPromise) return cashfreeSDKPromise;

    cashfreeSDKPromise = new Promise((resolve, reject) => {
        if (window.Cashfree) {
            resolve(window.Cashfree);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        script.onload = () => {
            if (window.Cashfree) {
                resolve(window.Cashfree);
            } else {
                reject(new Error('Cashfree SDK failed to initialize'));
            }
        };
        script.onerror = () => reject(new Error('Failed to load Cashfree SDK'));
        document.head.appendChild(script);
    });

    return cashfreeSDKPromise;
}

// ─── Create Session via Supabase Edge Function (Secure) ─────────────────────

export async function createCashfreeSession({
    amount,
    orderId,
    customerName,
    customerEmail,
    customerPhone,
    description = 'GetYourQR Payment',
}) {
    const { supabase } = await import('../supabaseClient');

    // Cashfree production requires a valid 10-digit Indian phone number
    const sanitizedPhone = String(customerPhone || '').replace(/\D/g, '').slice(-10).padStart(10, '9');



    const { data, error } = await supabase.functions.invoke('create-cashfree-session', {
        body: {
            amount: parseFloat(amount),
            orderId,
            customerName: customerName || 'Guest',
            customerEmail: customerEmail || `order_${orderId}@getyourqr.in`,
            customerPhone: sanitizedPhone,
            description,
        }
    });



    if (error) {
        console.error('[Cashfree] Edge Function invocation error:', error);
        throw new Error(`Backend error: ${error.message || JSON.stringify(error)}`);
    }

    if (data?.error) {
        console.error('[Cashfree] Cashfree API returned error:', data.error);
        throw new Error(`Cashfree error: ${data.error}`);
    }

    if (!data || !data.payment_session_id) {
        console.error('[Cashfree] No payment_session_id in response:', data);
        throw new Error('No payment session ID received from backend. Please try again.');
    }


    return data.payment_session_id;
}

// ─── Open Cashfree Checkout (Full-Page Redirect — no iframe/ad blocker issues) ──

export async function openCashfreeCheckout({
    amount,
    orderId,
    customerName,
    customerEmail,
    customerPhone,
    description,
    paymentType = 'sub',   // 'sub' for subscription, 'order' for food orders
    onFailure,
}) {
    try {
        // 1. Load the Cashfree JS SDK
        await loadCashfreeSDK();

        // 2. Create the payment session via secure backend
        const paymentSessionId = await createCashfreeSession({
            amount,
            orderId,
            customerName,
            customerEmail,
            customerPhone,
            description,
        });

        // 3. Initialize Cashfree SDK with environment
        const cashfree = await window.Cashfree({ mode: CASHFREE_ENV });

        // 4. Build the return URL — Cashfree will redirect here after payment
        //    We embed the order_id and type so the return page knows what to verify
        const returnUrl = `${window.location.origin}/payment/return?order_id=${orderId}&type=${paymentType}`;

        const checkoutOptions = {
            paymentSessionId,
            redirectTarget: '_self',  // Full-page redirect — works everywhere, no ad blocker issues
            returnUrl,
        };

        // This redirects the page — the return URL handles success/failure
        cashfree.checkout(checkoutOptions);

    } catch (err) {
        console.error('[Cashfree] Error in openCashfreeCheckout:', err);
        if (onFailure) onFailure(err);
    }
}
